import { Check, ClipboardCopy } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/Button";
import { DetailList } from "@/components/DetailList";
import { ErrorState } from "@/components/ErrorState";
import { LoadingState } from "@/components/LoadingState";
import { Modal, ModalTitle } from "@/components/Modal";
import { TextInput } from "@/components/TextInput";
import { useApi } from "@/lib/api";
import { formatDateTime } from "@/lib/formatDateTime";
import { useCopyToClipboard } from "@/lib/useCopyToClipboard";

interface HostIdentity {
  kind: string;
  fingerprint: string;
  status: string;
  source: string;
  isAuthenticated: boolean;
  firstSeenAt: string;
  lastSeenAt: string;
  approvedAt: string | null;
  effectivePolicy: string;
}

const SOURCE_LABELS: Record<string, string> = {
  Provisioned: "Taken from the host itself",
  Approved: "Approved by an administrator",
  Enrolled: "Trusted on first contact",
};

const VERIFY_COMMANDS: Record<string, string> = {
  SshHostKey:
    "for f in /etc/ssh/ssh_host_*_key.pub; do ssh-keygen -lf $f; done",
  X509PublicKey:
    '[Convert]::ToBase64String((Get-ChildItem "Cert:\\LocalMachine\\Remote Desktop" | Select-Object -First 1).RawData)',
};

const KUBERNETES_VERIFY_COMMAND = "cat /etc/kubernetes/pki/ca.crt";

function kindForConnectionMethod(
  connectionMethod: string | null,
): string | null {
  if (connectionMethod === "RDP") return "X509PublicKey";
  if (connectionMethod === "SSH") return "SshHostKey";
  if (connectionMethod === "KubernetesAPI" || isDatabase(connectionMethod))
    return "X509CertificateAuthority";

  return null;
}

export function HostIdentityModal({
  orgId,
  resourceId,
  resourceName,
  connectionMethod,
  onDone,
  onClose,
}: {
  orgId: string;
  resourceId: string;
  resourceName: string;
  connectionMethod: string | null;
  onDone: (message: string) => void;
  onClose: () => void;
}) {
  const api = useApi();
  const [identity, setIdentity] = useState<HostIdentity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [fingerprint, setFingerprint] = useState("");

  const kind = kindForConnectionMethod(connectionMethod);
  const provisionLabel = provisionLabelFor(kind, connectionMethod);

  const basePath = `/organizations/${orgId}/resources/${resourceId}/host-identity`;

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);

      const result = await api.get<HostIdentity>(basePath);

      if (cancelled) return;

      setIdentity(result);
      setLoading(false);
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [api, basePath]);

  async function act(path: string, method: "POST" | "DELETE", done: string) {
    setBusy(true);
    setError(null);

    try {
      await api.send(path, method);
      onDone(done);
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "That didn't work.");
    } finally {
      setBusy(false);
    }
  }

  async function provision() {
    setBusy(true);
    setError(null);

    try {
      await api.send(
        basePath,
        "PUT",
        kind === "SshHostKey"
          ? { kind, fingerprint: fingerprint.trim() }
          : { kind, certificate: fingerprint.trim() },
      );
      onClose();
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "That didn't work.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose}>
      <div className="mb-4 shrink-0">
        <ModalTitle>Host identity</ModalTitle>

        <p className="text-sm text-foreground/70">{resourceName}</p>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        {error && <ErrorState message={error} />}

        {loading ? (
          <LoadingState />
        ) : provisioning ? (
          <ProvisionForm
            kind={kind}
            connectionMethod={connectionMethod}
            command={
              connectionMethod === "KubernetesAPI"
                ? KUBERNETES_VERIFY_COMMAND
                : kind && VERIFY_COMMANDS[kind]
            }
            fingerprint={fingerprint}
            onFingerprintChange={setFingerprint}
          />
        ) : identity === null ? (
          <p className="text-sm text-foreground/70">
            No host identity recorded yet. Tendra will capture one automatically
            before any credentials are sent, either shortly after the resource
            is added or on the first connection, whichever comes first.
          </p>
        ) : (
          <div className="space-y-4">
            <Details
              identity={identity}
              provisionKind={kind}
              connectionMethod={connectionMethod}
            />
            <section className="flex flex-wrap items-center justify-between gap-6 rounded-lg border border-danger/30 p-4">
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-foreground">
                  Reset host identity
                </h3>
                <p className="mt-0.5 text-xs text-foreground">
                  Resetting removes the current host identity so Tendra can
                  trust a new one. Use this if the host has been rebuilt or its
                  identity has changed.
                </p>
              </div>
              <Button
                variant="dangerOutline"
                disabled={busy}
                className="shrink-0"
                onClick={() =>
                  act(
                    basePath,
                    "DELETE",
                    `Host identity for ${resourceName} reset.`,
                  )
                }
              >
                Reset host identity
              </Button>
            </section>
          </div>
        )}
      </div>

      <div className="mt-4 flex shrink-0 justify-end gap-3">
        <div className="flex gap-2">
          {provisioning ? (
            <Button
              variant="primary"
              disabled={
                busy || kind === null || fingerprint.trim().length === 0
              }
              onClick={provision}
            >
              {kind === "X509CertificateAuthority"
                ? "Save certificate authority"
                : "Save fingerprint"}
            </Button>
          ) : (
            <>
              {identity?.status === "Discovered" && (
                <Button
                  variant="primary"
                  disabled={busy}
                  onClick={() =>
                    act(
                      `${basePath}/approve`,
                      "POST",
                      `Host identity for ${resourceName} approved.`,
                    )
                  }
                >
                  Approve
                </Button>
              )}

              <Button disabled={busy} onClick={() => setProvisioning(true)}>
                {provisionLabel}
              </Button>
            </>
          )}
        </div>

        <Button onClick={provisioning ? () => setProvisioning(false) : onClose}>
          {provisioning ? "Back" : "Close"}
        </Button>
      </div>
    </Modal>
  );
}

function CommandBlock({ command }: { command: string }) {
  const { copiedField, copyToClipboard } = useCopyToClipboard();
  const copied = copiedField === command;

  return (
    <div className="relative">
      <code className="block overflow-x-auto rounded-md border border-muted bg-surface py-3 pr-12 pl-3 text-sm whitespace-pre">
        {command}
      </code>
      <Button
        variant="secondary"
        size="icon"
        onClick={() => copyToClipboard(command, command)}
        aria-label={copied ? "Copied" : "Copy command"}
        title={copied ? "Copied" : "Copy command"}
        className="absolute top-[7px] right-[7px]"
      >
        {copied ? (
          <Check className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
        ) : (
          <ClipboardCopy
            className="h-4 w-4"
            strokeWidth={1.8}
            aria-hidden="true"
          />
        )}
      </Button>
    </div>
  );
}

function isDatabase(connectionMethod: string | null): boolean {
  return (
    connectionMethod === "PostgresWireProtocol" ||
    connectionMethod === "MySQLProtocol"
  );
}

function hostNoun(connectionMethod: string | null): string {
  return isDatabase(connectionMethod) ? "server" : "cluster";
}

function provisionLabelFor(
  kind: string | null,
  connectionMethod: string | null,
): string {
  return kind === "X509CertificateAuthority"
    ? `Enter the ${hostNoun(connectionMethod)}'s CA`
    : "Enter a known fingerprint";
}

function Details({
  identity,
  provisionKind,
  connectionMethod,
}: {
  identity: HostIdentity;
  provisionKind: string | null;
  connectionMethod: string | null;
}) {
  const verifyCommand =
    identity.kind === "SshHostKey" ? VERIFY_COMMANDS[identity.kind] : null;

  return (
    <div className="space-y-4 text-sm">
      {!identity.isAuthenticated && (
        <p className="rounded-lg border border-muted bg-surface p-3">
          Tendra recorded this identity the first time it connected, and has
          required it to match ever since. Nobody has compared it with the host,
          which is how the default policy works. You can do that below if you
          want proof of which host answered.
        </p>
      )}

      {identity.status === "Discovered" && (
        <p className="rounded-lg border border-muted bg-surface p-3">
          Recorded but not yet approved. Sessions to this resource are refused
          until an administrator approves it.
        </p>
      )}

      <DetailList
        rows={[
          {
            label: "Fingerprint",
            value: (
              <span className="font-mono text-xs break-all">
                {identity.fingerprint}
              </span>
            ),
          },
          {
            label: "Established",
            value: SOURCE_LABELS[identity.source] ?? identity.source,
          },
          { label: "First seen", value: formatDateTime(identity.firstSeenAt) },
          { label: "Last seen", value: formatDateTime(identity.lastSeenAt) },
          ...(identity.approvedAt !== null
            ? [
                {
                  label: "Approved",
                  value: formatDateTime(identity.approvedAt),
                },
              ]
            : []),
          { label: "Policy", value: identity.effectivePolicy },
        ]}
      />

      {verifyCommand ? (
        <div>
          <p className="mb-1 text-foreground">
            Verify on the host that the fingerprint matches the one shown above.
          </p>
          <CommandBlock command={verifyCommand} />
        </div>
      ) : (
        <p className="text-foreground">
          {provisionKind === "X509CertificateAuthority"
            ? `To confirm which ${hostNoun(connectionMethod)} answers, enter its certificate authority`
            : "To confirm this identity against the host, export its certificate and enter it"}{" "}
          under {'"'}
          {provisionLabelFor(provisionKind, connectionMethod)}
          {'"'}. Tendra records it as authenticated.
        </p>
      )}
    </div>
  );
}

function ProvisionForm({
  kind,
  connectionMethod,
  command,
  fingerprint,
  onFingerprintChange,
}: {
  kind: string | null;
  connectionMethod: string | null;
  command: string | null | undefined;
  fingerprint: string;
  onFingerprintChange: (fingerprint: string) => void;
}) {
  const isAuthority = kind === "X509CertificateAuthority";
  const isCertificate = kind === "X509PublicKey" || isAuthority;

  return (
    <div className="space-y-4 text-sm">
      <p className="text-foreground/70">
        {isAuthority && isDatabase(connectionMethod)
          ? "Paste the certificate authority that issues the server's certificate, such as an Amazon RDS certificate bundle or a Cloud SQL server CA. Tendra trusts any certificate it issued for this server's address. It replaces any identity already recorded."
          : isAuthority
            ? "Paste the cluster's certificate authority, as shown in the EKS console or in kubeconfig's certificate-authority-data, or run the following command on a control plane node. Tendra trusts any API server certificate it issued for this cluster's address. It replaces any identity already recorded."
            : isCertificate
              ? "Run the following command on the host to export its certificate, then paste the result below. Tendra derives the identity from it and treats it as authenticated, because it came from the host rather than from the connection. It replaces any identity already recorded."
              : "Run the following command on the host to obtain its fingerprint. An identity entered here is one Tendra treats as authenticated, because it came from the host rather than from the connection. It replaces any identity already recorded."}
      </p>

      {command && <CommandBlock command={command} />}

      <div>
        <label
          className="mb-1.5 block text-sm text-foreground"
          htmlFor="host-identity-fingerprint"
        >
          {isAuthority
            ? "Certificate authority"
            : isCertificate
              ? "Certificate"
              : "Fingerprint"}
        </label>
        {isCertificate ? (
          <textarea
            id="host-identity-fingerprint"
            value={fingerprint}
            onChange={(e) => onFingerprintChange(e.target.value)}
            rows={5}
            placeholder="MIIDBTCCAe2gAwIBAgIQ... or a PEM certificate"
            className="w-full rounded-lg border border-muted bg-surface px-3 py-2 font-mono text-xs break-all text-foreground focus:border-accent focus:ring-2 focus:ring-accent/20 focus:outline-none"
          />
        ) : (
          <TextInput
            id="host-identity-fingerprint"
            value={fingerprint}
            onChange={(e) => onFingerprintChange(e.target.value)}
            placeholder="SHA256:..."
            className="w-full font-mono text-xs"
          />
        )}
      </div>
    </div>
  );
}
